// src/Components/Luna/LunaBurstLayer.tsx
// Draws the stardust flights of lunaPowerStore: a handful of sparks (the
// stardust art plus small glow motes) leave from where the learner tapped,
// fan out, and curve into the Luna ring. Transform and opacity only. Mounted
// once (LunaPowerHost) above every overlay.
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import Portal from "../Utils/Portal";
import { FLIGHT_MS, LunaFlight } from "./lunaPowerStore";
import { useLunaStore } from "./useLuna";

const STARDUST = "https://moonrock-images.s3.us-east-2.amazonaws.com/stardust-icon-clean.png";

/** Deterministic pseudo random per flight and particle, so renders are stable. */
function rand(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

const Flight: React.FC<{ f: LunaFlight }> = ({ f }) => {
  const parts = useMemo(
    () =>
      Array.from({ length: f.count }, (_, i) => {
        const a = rand(f.id * 31 + i) * Math.PI * 2;
        const spread = 34 + rand(f.id * 17 + i) * 46;
        const burst = { x: f.from.x + Math.cos(a) * spread, y: f.from.y + Math.sin(a) * spread * 0.7 - 18 };
        // Curve toward the ring through a point lifted above the straight line.
        const mid = {
          x: (burst.x + f.to.x) / 2 + (rand(f.id + i * 7) - 0.5) * 80,
          y: Math.min(burst.y, f.to.y) - 30 - rand(f.id * 3 + i) * 50,
        };
        const art = i % 2 === 0;
        return {
          i,
          art,
          size: art ? 28 + rand(f.id + i) * 10 : 10 + rand(f.id * 5 + i) * 6,
          path: [f.from, burst, mid, f.to],
          delay: i * 0.034,
          spin: (rand(f.id * 11 + i) - 0.5) * 240,
        };
      }),
    [f],
  );
  const dur = FLIGHT_MS / 1000;
  return (
    <>
      {/* A soft flash where the stardust leaves from. */}
      <motion.span
        className={`luna-burst-pop ${f.boosted ? "is-boosted" : ""}`}
        style={{ left: f.from.x, top: f.from.y }}
        initial={{ scale: 0.3, opacity: 0.95 }}
        animate={{ scale: 1.7, opacity: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        aria-hidden="true"
      />
      {parts.map((p) => (
        <motion.span
          key={p.i}
          className={`luna-spark ${p.art ? "luna-spark--art" : ""} ${f.boosted ? "is-boosted" : ""}`}
          style={{ width: p.size, height: p.size, marginLeft: -p.size / 2, marginTop: -p.size / 2 }}
          initial={{ x: p.path[0].x, y: p.path[0].y, scale: 0.3, opacity: 0, rotate: 0 }}
          animate={{
            x: p.path.map((q) => q.x),
            y: p.path.map((q) => q.y),
            scale: [0.3, 1.15, 0.95, 0.35],
            opacity: [0, 1, 1, 0.9],
            rotate: [0, p.spin * 0.4, p.spin * 0.8, p.spin],
          }}
          transition={{ duration: dur, delay: p.delay, times: [0, 0.22, 0.62, 1], ease: "easeInOut" }}
          aria-hidden="true"
        >
          {p.art ? <img src={STARDUST} alt="" draggable={false} /> : null}
        </motion.span>
      ))}
    </>
  );
};

const LunaBurstLayer: React.FC = () => {
  const { flights } = useLunaStore();
  if (!flights.length) return null;
  return (
    <Portal containerId="luna-burst-root">
      <div className="luna-burst-layer" aria-hidden="true">
        {flights.map((f) => (
          <Flight key={f.id} f={f} />
        ))}
      </div>
    </Portal>
  );
};

export default LunaBurstLayer;
